import React, { Fragment } from 'react';
import { G, Path } from '@react-pdf/renderer';
import MuiTableConstants from 'config/tables/tableConstants';
import { furnitureIcons } from '../../globalConstants/Icons/Report';
import { mapValues, clamp, round, isFunction } from 'lodash';

/**
 * Interface for table size
 */
interface TableSize {
  length: number;
  width: number;
  [key: string]: number;
}

/**
 * Interface for transform data
 */
interface Transform {
  x: number;
  y: number;
  a: number;
}

/**
 * Interface for table configuration
 */
interface TableConfig {
  shape: string;
  size: TableSize;
  transform: Transform;
  ghostChairs: number[];
}

/**
 * Interface for area data
 */
interface Area {
  table: TableConfig;
  [key: string]: any;
}

/**
 * Interface for areas object
 */
interface Areas {
  [key: string]: Area;
}

/**
 * Interface for content
 */
interface Content {
  areas: Areas;
  [key: string]: any;
}

/**
 * Interface for bounding box
 */
interface BoundingBox {
  width: number;
  height: number;
}

/**
 * Interface for table path props result
 */
interface TablePathProps {
  corner?: number | null;
  leftCorner?: number | null;
  rightCorner?: number | null;
  length?: number;
  bow?: number | null;
  width?: number;
}

/**
 * Interface for chair attributes
 */
interface ChairAttributes {
  id: string;
  x: number;
  y: number;
  a: number;
  [key: string]: any;
}

/**
 * Interface for chair array props
 */
interface ChairArrayProps {
  chairArray: ChairAttributes[];
  chairSpacing: { x: number; y: number };
}

/**
 * Interface for table definition
 */
interface TableDefinition {
  notched?: boolean;
  maxSlope?: number | null;
  pathProps: (size: TableSize) => TablePathProps;
  chairs: (size: TableSize, chair: any, slope: number) => ChairArrayProps;
}

/**
 * Interface for MuiFurniture props
 */
interface MuiFurnitureProps {
  areaId: string;
  bbox: BoundingBox;
  scale: number;
  content: Content;
}

/**
 * MUI Furniture component that replaces the styled-components Furniture component
 * 
 * This component provides a Material UI equivalent of the original Furniture component
 * with similar props and functionality. It's used for PDF generation.
 * 
 * @param {MuiFurnitureProps} props
 * @returns {JSX.Element} MUI Furniture component
 */
const MuiFurniture: React.FC<MuiFurnitureProps> = (props) => {
  const { areaId, bbox, scale, content: { areas } } = props;
  const { table: { shape, size, transform, ghostChairs } } = areas[areaId];
  const tableSize = mapValues(size, (v) => v * scale);

  // Table Center
  const cx = round(tableSize.length * 2) / 4;
  const cy = round(tableSize.width * 2) / 4;

  // Table transormation
  const tx = (bbox.width / 2) + transform.x * scale;
  const ty = (bbox.height / 2) + transform.y * scale;

  // Define the table path
  const tableConstantsTables = MuiTableConstants.tables as unknown as Record<string, TableDefinition>;
  const { notched = false, maxSlope = null, pathProps = () => ({} as TablePathProps), chairs = () => ({ chairArray: [], chairSpacing: { x: 0, y: 0 } }) } = tableConstantsTables[shape] ?? {};
  const { corner = null, leftCorner = null, rightCorner = null, length = tableSize.length, bow = null, width = tableSize.width } = pathProps(tableSize) as TablePathProps;
  const slope = clamp((tableSize.width / 2 - 0.4572 * scale) / length, 0, maxSlope ?? Infinity);
  const legWidth = 0.6096 * scale;
  const legLength = length - legWidth;
  const x0 = cx - length / 2;
  const x1 = slope ? (width / 2 - legWidth + (corner ?? 0) + slope * x0) / slope : x0;
  const innerTapper = slope * Math.min(x1 - x0, legLength);
  const innerCorner = maxSlope && (x1 - x0 <= legLength) ? 0 : clamp(width - legWidth * 2 + (corner ?? 0) * 2, 0, corner ?? 0) / 2;
  const inner = (notched && width >= (legWidth - (corner ?? 0)) * 2 && innerTapper >= 0) ? [
    `v${(corner ?? 0) * 2 - legWidth}`,
    `a${corner},${corner} 0 0 1 ${corner},-${corner}`,
    `l${Math.min(legLength + innerCorner, maxSlope ? x1 - x0 : length)} ${-innerTapper}`,
    `a${innerCorner},${innerCorner} 0 0 0 ${innerCorner},-${innerCorner}`,
    `v${maxSlope && (x1 - x0 <= legLength) ? 0 : (legWidth - (corner ?? 0) + innerCorner + innerTapper) * 2 - width}`,
    `a${innerCorner},${innerCorner} 0 0 0 -${innerCorner},-${innerCorner}`,
    `l${-Math.min(legLength + innerCorner, maxSlope ? x1 - x0 : length)} ${-innerTapper}`,
    `a${corner},${corner} 0 0 1 -${corner},-${corner}`,
    `v${(corner ?? 0) * 2 - legWidth}`] :
    [`v${- width}`];

  const tapper = maxSlope ? slope * length : 0;
  const rightWidth = maxSlope ? width - tapper * 2 : rightCorner ? 0 : null;
  const originX = cx - (length / 2) - (rightCorner ?? 0) / 2 + (leftCorner ?? 0) / 2;
  const originY = cy - (width / 2) - (leftCorner ?? corner ?? 0);

  const path = [
    `m ${originX} ${originY}`,
    `q ${length / 2},${bow ? -bow : tapper / 2} ${length},${tapper}`,
    `a${rightCorner ?? corner},${rightCorner ?? corner} 0 0 1 ${rightCorner ?? corner},${rightCorner ?? corner}`,
    `v${rightWidth ?? width}`,
    `a${rightCorner ?? corner},${rightCorner ?? corner} 0 0 1 -${rightCorner ?? corner},${rightCorner ?? corner}`,
    `q${-length / 2},${bow ?? tapper / 2} ${-length},${tapper}`,
    `a${leftCorner ?? corner},${leftCorner ?? corner} 0 0 1 -${leftCorner ?? corner},-${leftCorner ?? corner}`,
    `${inner.join('')}`,
    `a${leftCorner ?? corner},${leftCorner ?? corner} 0 0 1 ${leftCorner ?? corner},-${leftCorner ?? corner}z`,
  ].join('');

  // Add chairs
  const chair = mapValues(MuiTableConstants.chair, (v) => isFunction(v) ? v : v * scale);
  const { chairArray = [] } = chairs(tableSize, chair, slope);
  const Chair = furnitureIcons.chair as React.ComponentType<any>;

  return (
    <Fragment>
      <G transform={`translate(${round(tx)}, ${round(ty)})`}>
        <G transform={`rotate(${transform.a})`} >
          <Path d={path} transform={`translate(${round(-cx)}, ${round(-cy)})`} stroke='#000' />
          {chairArray.map((ch, i) => {
            // Filter out ghosted chairs
            if (ghostChairs.includes(ch.id)) return null;
            const angle = transform.a * Math.PI / 180;
            const xx = ch.x * Math.cos(angle) - ch.y * Math.sin(angle) + tx;
            const yy = ch.x * Math.sin(angle) + ch.y * Math.cos(angle) + ty;
            const rr = (chair.size as number) * .51;
            return (xx - rr >= 0 && xx + rr <= bbox.width && yy - rr >= 0 && yy + rr <= bbox.height) && (
              <Chair key={ch.id} arms={true} size={chair.size} {...ch} />
            );
          })}
        </G>
      </G>
    </Fragment>
  );
};

export default MuiFurniture;
